import { KeyboardArrowDown, Sort } from '@mui/icons-material';
import {
  Checkbox,
  CircularProgress,
  Grid,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import Modal from 'react-modal';
import { IFinancialReportViewModel, ISolicitationViewModel } from 'viewModels';
import { useTheme } from 'styled-components';
import { ReactComponent as IconExcelReport } from '../../assets/icons/icon-excel-report.svg';
import { ReactComponent as IconClose } from '../../assets/icons/IconCloseCircle.svg';
import { ReactComponent as IconDollar } from '../../assets/icons/iconDollar.svg';
import { ReactComponent as IconUnpaid } from '../../assets/icons/unpaid.svg';
import { ReactComponent as IconPaidOut } from '../../assets/icons/IconPaidOut.svg';
import { ReactComponent as IconUpload } from '../../assets/icons/IconUpload.svg';

import { useApp, useAuth } from '../../context';
import {
  CorrespondenceType,
  ModalPaymentType,
  PaymentSolicitationStatus,
  ServiceStatus,
  TutorialType,
} from '../../types/enums';
import { Button } from '../Buttons/Button';
// import ButtonImportXLSX from '../ButtonImportXLSX';
import { ModalPaymentMultiple } from '../Modals/ModalPaymentMultiple';
import { ModalStatusPayment } from '../Modals/ModalStatusPayment';
import { Body, Container, Content, FlagStatus, Header, ModalContent, ModalHeader } from './styles';
import { useSolicitation } from '../../context/solicitation';
import useWebSocket from '../../hooks/useWebSocket';
import { BaseFilterLeft } from '../BaseFilterLeft';
import { usePayment } from '../../context/payment';
import { useReport } from '../../context/report';
import { useAlert } from '../../hooks/useAlert';
import { SolicitationController } from '../../controller/SolicitationController';
import TitlePage from '../TitlePage';
import JoyrideTour from '../JoyrideTour';
import { financialSteps } from '../../constants/Tour/Steps/financialSteps';

export function ReportFinancial() {
  const theme = useTheme();
  const CURRENT_DATE = new Date();
  const MAX_OF_DAYS_FOR_PAYMENT = 90;
  const { user } = useAuth();
  const { isGeneratingReport, handleGenerateFinancialReport } = useReport();
  const alert = useAlert();
  const app = useApp();

  const {
    payments,
    setPayments,
    filterPayments,
    setFilterPayments,
    isLoadingPayments,
    hasNextPagePayments,
    fetchNextPagePayments,
    isFetchingNextPagePayments,
    setPaymentOrderBy,
    setEnablePaymentsLoad,
    paymentOrderBy,
  } = usePayment();
  const { setSolicitations, isLoadingSolicitation } = useSolicitation();
  const [isActiveMultiPayment, setIsActiveMultiPayment] = useState(false);
  const [selectedSolicitation, setSelectedSolicitation] = useState<ISolicitationViewModel>();
  const [isOpenModalStatusPayment, setIsOpenModalStatusPayment] = useState(false);
  const [selectedPaymentIds, setSelectedPaymentIds] = useState<string[]>([]);
  const [isOpenModalStatusPaymentMultiple, setIsOpenModalStatusPaymentMultiple] = useState(false);
  const [runningTutorial, setRunningTutorial] = useState(false);

  const financialReportToTutorial: IFinancialReportViewModel[] = [
    {
      id: '123e4567-e89b-12d3-a456-426614174000',
      solicitationId: '456e7890-e12b-34d5-b678-526614174111',
      solicitationStatus: ServiceStatus.CONCLUED,
      value: 15,
      integratedProtocolValue: 30,
      createdAt: new Date('2025-01-15T00:00:00'),
      dateLimit: new Date('2025-02-15T00:00:00'),
      concludedAt: new Date(),
      processNumber: '00123',
      createdByName: 'Tutorial',
      paymentStatus: PaymentSolicitationStatus.UNPAID,
      clientName: 'Empresa Tutorial',
      protocolNumber: '3131312',
      correspondence: {
        id: '1',
        name: 'Correspondência Tutorial',
        type: CorrespondenceType.DILIGENCE,
      },
      correspondent: {
        firstName: 'Correspondente',
        lastName: 'Tutorial',
        id: '123e4567-e89b-12d3-a456-426614174000',
        email: 'tutorial@email.com',
        name: 'Correspondente Tutorial',
        address: {
          street: 'Rua do Tutorial',
          number: 123,
          city: 'Cidade do Tutorial',
          uf: 'ST',
          postalCode: '12345-678',
          latitude: 0,
          longitude: 0,
        },
      },
    },
  ];

  const countSelectedPayments = useMemo(
    () => selectedPaymentIds.length,
    [selectedPaymentIds.length],
  );

  const handleActiveMultiPayment = () => {
    setIsActiveMultiPayment(!isActiveMultiPayment);
  };

  const handleOpenModalStatusPayment = async (id: string) => {
    const solicitation = await SolicitationController.getById(id);
    if (!solicitation) return;
    if (solicitation.status === ServiceStatus.UNPAID) return;
    setSelectedSolicitation(solicitation);
    setIsOpenModalStatusPayment(true);
  };

  const handleClickCheckBoxPayment = (
    event: React.ChangeEvent<HTMLInputElement>,
    paymentId: string,
  ) => {
    if (event.target.checked) {
      const lengthPaymentsUnpaids = payments.filter(
        (p) => p.paymentStatus === PaymentSolicitationStatus.UNPAID,
      ).length;
      if (lengthPaymentsUnpaids !== selectedPaymentIds.length) {
        setSelectedPaymentIds((prev) =>
          paymentId === 'all'
            ? payments
                .filter(
                  (p) =>
                    p.paymentStatus === PaymentSolicitationStatus.UNPAID &&
                    p.solicitationStatus === ServiceStatus.CONCLUED,
                )
                .map((p) => p.solicitationId)
            : [...(prev ?? []), paymentId],
        );
      } else {
        setSelectedPaymentIds((prev) => (paymentId === 'all' ? [] : [...(prev ?? []), paymentId]));
      }
    } else {
      setSelectedPaymentIds((prev) =>
        paymentId === 'all' ? [] : (prev ?? []).filter((id) => id !== paymentId),
      );
    }
  };

  const onRequestExtract = () => {
    if (isGeneratingReport) return;
    alert.show({ message: 'Gerando relatório... Aguarde', type: 'success' });
    const solicitationIds = payments.map((payment) => payment.solicitationId);
    handleGenerateFinancialReport({
      solicitationIds,
      orderBy: paymentOrderBy,
      ...filterPayments,
    });
  };

  const handleSort = (key: string) => () => {
    setPaymentOrderBy((prev) => {
      const newOrder = [...prev];
      const oldOrderIdx = prev.findIndex((o) => o.key === key);
      if (oldOrderIdx === -1) {
        newOrder.push({
          key,
          direction: 'asc',
        });
      } else if (newOrder[oldOrderIdx].direction === 'asc') {
        newOrder[oldOrderIdx].direction = 'desc';
      } else {
        newOrder.splice(oldOrderIdx, 1);
      }
      return newOrder;
    });
  };

  const getSortIcon = (key: string) => (
    <IconButton size="small" onClick={handleSort(key)}>
      {paymentOrderBy.find((o) => o.key === key) ? (
        <KeyboardArrowDown
          sx={{
            transition: 'all 0.5s ease-in',
            transform:
              paymentOrderBy.find((o) => o.key === key)?.direction === 'asc'
                ? 'rotate(-180deg)'
                : '',
          }}
        />
      ) : (
        <Sort />
      )}
    </IconButton>
  );

  // const handleGetTemplate = () => {
  //   getTemplate('FINANCEIRO');
  //   setIsImportedTemplate(true);
  // };

  useWebSocket<{ id: string; status: PaymentSolicitationStatus }>({
    url: `solicitation/${user.id}/update-payment`,
    onMessage: (response) => {
      setSolicitations((prevSolicitations) => {
        const index = prevSolicitations.findIndex(
          (solicitation) => solicitation.id === response.id,
        );
        if (index !== -1) {
          const newSolicitation = { ...prevSolicitations[index] };
          newSolicitation.payment.paymentSolicitationStatus = response.status;
          return [
            ...prevSolicitations.slice(0, index),
            newSolicitation,
            ...prevSolicitations.slice(index + 1),
          ];
        }
        return prevSolicitations;
      });
      setPayments((prevPayments) => {
        const index = prevPayments.findIndex((payment) => payment.solicitationId === response.id);
        if (index !== -1) {
          const newPayment = { ...prevPayments[index] };
          newPayment.paymentStatus = response.status;
          return [...prevPayments.slice(0, index), newPayment, ...prevPayments.slice(index + 1)];
        }
        return prevPayments;
      });
    },
  });

  useEffect(() => {
    app.setLoading(isLoadingSolicitation || isLoadingPayments);
  }, [app, isLoadingPayments, isLoadingSolicitation]);

  useEffect(() => {
    setSelectedPaymentIds((prev) =>
      (prev ?? []).filter((id) => payments.some((p) => p.solicitationId === id)),
    );
  }, [payments]);

  useEffect(() => {
    setEnablePaymentsLoad(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Ao montar o componente habilita o carregamento dos pagamentos

  useEffect(() => {
    if (!user?.tutorialProgress?.some((p) => p === TutorialType.FINANCIAL_REPORT)) {
      setRunningTutorial(true);
    }
  }, [user.tutorialProgress]);

  return (
    <>
      <JoyrideTour
        steps={financialSteps}
        type={TutorialType.FINANCIAL_REPORT}
        run={runningTutorial}
        onCallbackFinished={() => setRunningTutorial(false)}
      />
      <Body>
        <BaseFilterLeft
          toFinancial
          onChangeFilter={(solicitationFilter) => setFilterPayments(solicitationFilter)}
          displayOptions={{
            client: true,
            processNumber: false,
            correspondent: true,
            correspondenceType: false,
            correspondence: true,
            date: true,
            especilty: false,
            status: false,
            audienceType: false,
            folderName: false,
            partName: false,
            court: false,
            protocol: true,
            paymentStatus: true,
            district: false,
          }}
        />
        <Container>
          <div className="titlePage">
            <TitlePage
              title="Financeiro"
              id="start-item"
              onRequestOpen={() => {
                setRunningTutorial(true);
              }}
            />
            <div className="containerExtractReport">
              <Button
                text="Extrair relatório"
                LeftComponent={IconExcelReport}
                styleLeftComponent={{ fill: theme.colors.tertiary_500 }}
                style={{ fontSize: '1.20rem', display: 'flex', alignItems: 'center' }}
                dimensions={{
                  width: '10rem',
                  height: '1.25rem',
                }}
                positionLeftElement={5}
                disabled={isGeneratingReport}
                border={{ color: 'background_white', width: 0 }}
                color="tertiary_500"
                textHoverColor="tertiary_500"
                backgroundHoverColor="background_white"
                onClick={onRequestExtract}
              />
            </div>
          </div>
          <div className="containerTable">
            <Header>
              <div className="containerActions">
                <Button
                  text="Multi pagamento"
                  className="multiPayment"
                  LeftComponent={IconDollar}
                  style={{
                    fontSize: '1.20rem',
                    display: 'flex',
                    alignItems: 'center',
                    textDecoration: 'underline',
                  }}
                  dimensions={{
                    width: '9.5rem',
                    height: '1.75rem',
                  }}
                  styleLeftComponent={{ fill: theme.colors.tertiary_500 }}
                  positionLeftElement={0}
                  border={{ color: 'transparent', width: 0 }}
                  color="tertiary_500"
                  textHoverColor="tertiary_500"
                  backgroundHoverColor="background"
                  onClick={() => {
                    handleActiveMultiPayment();
                    setSelectedPaymentIds([]);
                  }}
                />
              </div>
            </Header>
            <Content>
              <div className="tableFixHead">
                <Table>
                  <TableHead>
                    <TableRow>
                      {isActiveMultiPayment && (
                        <TableCell padding="checkbox">
                          <Checkbox
                            onChange={(e) => handleClickCheckBoxPayment(e, 'all')}
                            checked={selectedPaymentIds.length === payments.length}
                            indeterminate={
                              selectedPaymentIds.length !== payments.length &&
                              selectedPaymentIds.length > 0
                            }
                          />
                        </TableCell>
                      )}
                      <TableCell>
                        <p>{}</p>
                      </TableCell>
                      <TableCell>
                        <p>
                          Nº do Protocolo
                          {getSortIcon('protocolNumber')}
                        </p>
                      </TableCell>
                      <TableCell>
                        <p>
                          Correspondente
                          {getSortIcon('correspondent')}
                        </p>
                      </TableCell>
                      <TableCell>
                        <p>Correspondência {getSortIcon('correspondence')}</p>
                      </TableCell>
                      <TableCell>
                        <p>Cliente {getSortIcon('clientName')}</p>
                      </TableCell>
                      <TableCell>
                        <p>Criação {getSortIcon('createdAt')}</p>
                      </TableCell>
                      <TableCell>
                        <p>Valor {getSortIcon('value')}</p>
                      </TableCell>
                      <TableCell>
                        <p>Protocolo Integrado</p>
                      </TableCell>
                      <TableCell>
                        <p>Pagamento {getSortIcon('status')}</p>
                      </TableCell>
                      <TableCell>
                        <p>Prazo Pagamento</p>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  {runningTutorial ? (
                    <TableBody>
                      {financialReportToTutorial.map((payment) => (
                        <TableRow key={payment.solicitationId}>
                          {isActiveMultiPayment && (
                            <TableCell padding="checkbox">
                              <Checkbox
                                onChange={(e) =>
                                  handleClickCheckBoxPayment(e, payment.solicitationId)
                                }
                                checked={selectedPaymentIds.includes(payment.solicitationId)}
                                disabled={
                                  payment.paymentStatus === PaymentSolicitationStatus.PAID ||
                                  payment.solicitationStatus === ServiceStatus.UNPAID
                                }
                              />
                            </TableCell>
                          )}
                          <TableCell
                            style={{
                              padding: '0 5px 0 0',
                              minWidth: '0px',
                            }}>
                            <FlagStatus status={payment?.paymentStatus} />
                          </TableCell>
                          <TableCell>{payment.protocolNumber}</TableCell>
                          <TableCell
                            style={{
                              padding: '0 0 0 5px',
                            }}>
                            {payment.correspondent
                              ? `${payment.correspondent.firstName} ${payment.correspondent.lastName}`
                              : '-'}
                          </TableCell>
                          <TableCell>{payment?.correspondence?.name}</TableCell>
                          <TableCell>{payment.clientName}</TableCell>
                          <TableCell>
                            {new Date(payment.createdAt).toLocaleDateString('pt-BR', {
                              day: '2-digit',
                              month: '2-digit',
                              year: 'numeric',
                            })}
                          </TableCell>
                          <TableCell>R$ {payment.value}</TableCell>
                          <TableCell>R$ {payment?.integratedProtocolValue ?? 0}</TableCell>
                          <TableCell id="payment-status">
                            {payment.solicitationStatus === ServiceStatus.UNPAID ? (
                              <Tooltip
                                disableInteractive
                                title="Pagamento não realizado"
                                followCursor>
                                <div
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                  }}>
                                  <Button
                                    text=""
                                    style={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      alignSelf: 'center',
                                    }}
                                    styleLeftComponent={{
                                      width: '1.75rem',
                                      height: '2rem',
                                      fill: theme.colors.error,
                                      paddingLeft: '0.25rem',
                                    }}
                                    LeftComponent={IconUnpaid}
                                    dimensions={{
                                      width: '2.25rem',
                                      height: '2rem',
                                    }}
                                    border={{ color: 'background', width: 0 }}
                                    color="error"
                                    textHoverColor="error"
                                    backgroundHoverColor="background"
                                  />
                                </div>
                              </Tooltip>
                            ) : payment.paymentStatus === PaymentSolicitationStatus.PAID ? (
                              <Tooltip disableInteractive title="Pago" followCursor>
                                <div
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                  }}>
                                  <Button
                                    text=""
                                    style={{ display: 'flex', alignItems: 'center' }}
                                    LeftComponent={IconPaidOut}
                                    styleLeftComponent={{
                                      fill: theme.colors.primary,
                                      stroke: theme.colors.primary,
                                      width: '2rem',
                                      height: '1.5rem',
                                    }}
                                    dimensions={{
                                      width: '2.5rem',
                                      height: '2rem',
                                    }}
                                    border={{ color: 'background', width: 0 }}
                                    color="primary"
                                    textHoverColor="primary"
                                    backgroundHoverColor="background"
                                    onClick={() =>
                                      handleOpenModalStatusPayment(payment.solicitationId as string)
                                    }
                                  />
                                </div>
                              </Tooltip>
                            ) : (
                              <Tooltip disableInteractive title="Aguardando pagamento" followCursor>
                                <div
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                  }}>
                                  <Button
                                    text=""
                                    style={{ display: 'flex', alignItems: 'center' }}
                                    styleLeftComponent={{ width: '2rem', height: '1.25rem' }}
                                    LeftComponent={IconUpload}
                                    dimensions={{
                                      width: '2.5rem',
                                      height: '1.75rem',
                                    }}
                                    border={{ color: 'background', width: 0 }}
                                    color="error"
                                    textHoverColor="error"
                                    backgroundHoverColor="background"
                                    onClick={() =>
                                      handleOpenModalStatusPayment(payment.solicitationId as string)
                                    }
                                  />
                                </div>
                              </Tooltip>
                            )}
                          </TableCell>
                          <TableCell>
                            {payment.paymentStatus === PaymentSolicitationStatus.UNPAID &&
                            payment.concludedAt ? (
                              <div className="containerReaminingDays">
                                {(() => {
                                  const dateDiference = Math.abs(
                                    CURRENT_DATE.getTime() -
                                      new Date(payment.concludedAt).getTime(),
                                  );
                                  const days = Math.ceil(dateDiference / (1000 * 3600 * 24));
                                  const remainingDaysOfPayment = MAX_OF_DAYS_FOR_PAYMENT - days;
                                  return remainingDaysOfPayment >= 0 ? (
                                    <Typography
                                      color={
                                        remainingDaysOfPayment > 60
                                          ? 'tertiary'
                                          : remainingDaysOfPayment < 30
                                          ? 'red'
                                          : 'tertiary'
                                      }>
                                      {remainingDaysOfPayment} dias
                                    </Typography>
                                  ) : (
                                    <Typography color="red" noWrap textOverflow="ellipsis">
                                      -
                                    </Typography>
                                  );
                                })()}
                              </div>
                            ) : (
                              <div className="containerReaminingDays">
                                <p>-</p>
                              </div>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  ) : (
                    !runningTutorial && (
                      <TableBody>
                        {isLoadingPayments ? (
                          <TableRow>
                            <TableCell colSpan={8} align="center">
                              <Grid item alignItems="center" sx={{ minHeight: '65px !important' }}>
                                <CircularProgress size={15} />
                                <Typography
                                  paddingLeft={1}
                                  variant="caption"
                                  color="text"
                                  sx={{ fontStyle: 'italic' }}>
                                  Carregando lista de pagamentos...
                                </Typography>
                              </Grid>
                            </TableCell>
                          </TableRow>
                        ) : !payments.length ? (
                          <TableRow>
                            <TableCell colSpan={8} align="center">
                              <Typography
                                paddingLeft={1}
                                variant="caption"
                                color="text"
                                sx={{ fontStyle: 'italic' }}>
                                Nenhum pagamento encontrado...
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ) : (
                          payments.map((payment) => (
                            <TableRow key={payment.solicitationId}>
                              {isActiveMultiPayment && (
                                <TableCell padding="checkbox">
                                  <Checkbox
                                    onChange={(e) =>
                                      handleClickCheckBoxPayment(e, payment.solicitationId)
                                    }
                                    checked={selectedPaymentIds.includes(payment.solicitationId)}
                                    disabled={
                                      payment.paymentStatus === PaymentSolicitationStatus.PAID ||
                                      payment.solicitationStatus === ServiceStatus.UNPAID
                                    }
                                  />
                                </TableCell>
                              )}
                              <TableCell
                                style={{
                                  padding: '0 5px 0 0',
                                  minWidth: '0px',
                                }}>
                                <FlagStatus status={payment?.paymentStatus} />
                              </TableCell>
                              <TableCell>{payment.protocolNumber}</TableCell>
                              <TableCell
                                style={{
                                  padding: '0 0 0 5px',
                                }}>
                                {payment.correspondent
                                  ? `${payment.correspondent.firstName} ${payment.correspondent.lastName}`
                                  : '-'}
                              </TableCell>
                              <TableCell>{payment?.correspondence?.name}</TableCell>
                              <TableCell>{payment.clientName}</TableCell>
                              <TableCell>
                                {new Date(payment.createdAt).toLocaleDateString('pt-BR', {
                                  day: '2-digit',
                                  month: '2-digit',
                                  year: 'numeric',
                                })}
                              </TableCell>
                              <TableCell>R$ {payment.value}</TableCell>
                              <TableCell>R$ {payment?.integratedProtocolValue ?? 0}</TableCell>
                              <TableCell>
                                {payment.solicitationStatus === ServiceStatus.UNPAID ? (
                                  <Tooltip
                                    disableInteractive
                                    title="Pagamento não realizado"
                                    followCursor>
                                    <div
                                      style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                      }}>
                                      <Button
                                        text=""
                                        style={{
                                          display: 'flex',
                                          alignItems: 'center',
                                          alignSelf: 'center',
                                        }}
                                        styleLeftComponent={{
                                          width: '1.75rem',
                                          height: '2rem',
                                          fill: theme.colors.error,
                                          paddingLeft: '0.25rem',
                                        }}
                                        LeftComponent={IconUnpaid}
                                        dimensions={{
                                          width: '2.25rem',
                                          height: '2rem',
                                        }}
                                        border={{ color: 'background', width: 0 }}
                                        color="error"
                                        textHoverColor="error"
                                        backgroundHoverColor="background"
                                      />
                                    </div>
                                  </Tooltip>
                                ) : payment.paymentStatus === PaymentSolicitationStatus.PAID ? (
                                  <Tooltip disableInteractive title="Pago" followCursor>
                                    <div
                                      style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                      }}>
                                      <Button
                                        text=""
                                        style={{ display: 'flex', alignItems: 'center' }}
                                        LeftComponent={IconPaidOut}
                                        styleLeftComponent={{
                                          fill: theme.colors.primary,
                                          stroke: theme.colors.primary,
                                          width: '2rem',
                                          height: '1.5rem',
                                        }}
                                        dimensions={{
                                          width: '2.5rem',
                                          height: '2rem',
                                        }}
                                        border={{ color: 'background', width: 0 }}
                                        color="primary"
                                        textHoverColor="primary"
                                        backgroundHoverColor="background"
                                        onClick={() =>
                                          handleOpenModalStatusPayment(
                                            payment.solicitationId as string,
                                          )
                                        }
                                      />
                                    </div>
                                  </Tooltip>
                                ) : (
                                  <Tooltip
                                    disableInteractive
                                    title="Aguardando pagamento"
                                    followCursor>
                                    <div
                                      style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                      }}>
                                      <Button
                                        text=""
                                        style={{ display: 'flex', alignItems: 'center' }}
                                        styleLeftComponent={{ width: '2rem', height: '1.25rem' }}
                                        LeftComponent={IconUpload}
                                        dimensions={{
                                          width: '2.5rem',
                                          height: '1.75rem',
                                        }}
                                        border={{ color: 'background', width: 0 }}
                                        color="error"
                                        textHoverColor="error"
                                        backgroundHoverColor="background"
                                        onClick={() =>
                                          handleOpenModalStatusPayment(
                                            payment.solicitationId as string,
                                          )
                                        }
                                      />
                                    </div>
                                  </Tooltip>
                                )}
                              </TableCell>
                              <TableCell>
                                {payment.paymentStatus === PaymentSolicitationStatus.UNPAID &&
                                payment.concludedAt ? (
                                  <div className="containerReaminingDays">
                                    {(() => {
                                      const dateDiference = Math.abs(
                                        CURRENT_DATE.getTime() -
                                          new Date(payment.concludedAt).getTime(),
                                      );
                                      const days = Math.ceil(dateDiference / (1000 * 3600 * 24));
                                      const remainingDaysOfPayment = MAX_OF_DAYS_FOR_PAYMENT - days;
                                      return remainingDaysOfPayment >= 0 ? (
                                        <Typography
                                          color={
                                            remainingDaysOfPayment > 60
                                              ? 'tertiary'
                                              : remainingDaysOfPayment < 30
                                              ? 'red'
                                              : 'tertiary'
                                          }>
                                          {remainingDaysOfPayment} dias
                                        </Typography>
                                      ) : (
                                        <Typography color="red" noWrap textOverflow="ellipsis">
                                          -
                                        </Typography>
                                      );
                                    })()}
                                  </div>
                                ) : (
                                  <div className="containerReaminingDays">
                                    <p>-</p>
                                  </div>
                                )}
                              </TableCell>
                            </TableRow>
                          ))
                        )}
                        {hasNextPagePayments && (
                          <TableRow>
                            <TableCell colSpan={8} align="center">
                              {isFetchingNextPagePayments ? (
                                <Grid
                                  item
                                  alignItems="center"
                                  sx={{ minHeight: '65px !important' }}>
                                  <CircularProgress size={15} />
                                  <Typography
                                    paddingLeft={1}
                                    variant="caption"
                                    color="text"
                                    sx={{ fontStyle: 'italic' }}>
                                    Carregando itens...
                                  </Typography>
                                </Grid>
                              ) : (
                                <Grid item justifyContent="center" sx={{ display: 'flex' }}>
                                  <Button
                                    onClick={() => fetchNextPagePayments()}
                                    text="Carregar mais..."
                                    dimensions={{
                                      height: '2rem',
                                      width: '10rem',
                                    }}
                                  />
                                </Grid>
                              )}
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    )
                  )}
                </Table>
              </div>
            </Content>
          </div>
        </Container>
        {selectedSolicitation && (
          <ModalStatusPayment
            isOpen={isOpenModalStatusPayment}
            onRequestClose={() => {
              setIsOpenModalStatusPayment(false);
              setSelectedSolicitation(undefined);
            }}
            selectedPayment={selectedSolicitation.payment}
            type={ModalPaymentType.UPDATE_PAYMENT}
          />
        )}
        {isOpenModalStatusPaymentMultiple && (
          <ModalPaymentMultiple
            isOpen={isOpenModalStatusPaymentMultiple}
            onRequestClose={() => {
              setIsOpenModalStatusPaymentMultiple(false);
              setSelectedPaymentIds([]);
            }}
            solicitationsIds={selectedPaymentIds}
          />
        )}
      </Body>

      <Modal
        isOpen={isActiveMultiPayment}
        onRequestClose={() => setIsActiveMultiPayment(false)}
        style={{
          overlay: {
            position: 'absolute',
            right: window.innerWidth <= 470 ? '5px' : '25px',
            bottom: '0px',
            left: 'auto',
            top: 'auto',
            borderRadius: '2rem 2rem 0 0',
          },
          content: {
            position: 'relative',
            bottom: '0px',
            right: '20px',
            left: '0px',
            top: '0px',
            background: theme.colors.background_white,
            overflow: 'auto',
            WebkitOverflowScrolling: 'touch',
            borderRadius: '2rem 2rem 0 0',
            outline: 'none',
            padding: '20px',
            width: window.innerWidth <= 470 ? '230px' : '377px',
            height: window.innerWidth <= 470 ? '150px' : '200px',
            boxShadow: '0px -3px 6px rgba(0, 0, 0, 0.16)',
          },
        }}>
        <ModalHeader>
          <div className="title">
            <IconDollar />
            <h2>Multi Pagamento</h2>
          </div>
          <button
            className="close-modal"
            type="button"
            onClick={() => {
              setIsActiveMultiPayment(false);
              setSelectedPaymentIds([]);
            }}>
            <IconClose />
            Cancelar
          </button>
        </ModalHeader>
        <ModalContent>
          <p>
            <strong>{countSelectedPayments}</strong> solicitações selecionadas
          </p>
          <Button
            text="Alterar Status / Anexar comprovante"
            style={{ display: 'flex', alignItems: 'center' }}
            dimensions={{
              width: '18rem',
              height: '2rem',
            }}
            positionLeftElement={2}
            border={{ color: 'background', width: 0 }}
            color="background_white"
            className="button"
            background="secondary"
            backgroundHoverColor="primary"
            onClick={() => setIsOpenModalStatusPaymentMultiple(true)}
            disabled={countSelectedPayments === 0}
          />
        </ModalContent>
      </Modal>
    </>
  );
}
